import React from 'react';
import {
	Box,
	Heading,
	Text,
	Button,
	VStack,
	useColorModeValue,
	Container,
	Flex,
	Icon,
	useBreakpointValue,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { WarningTwoIcon, LockIcon } from '@chakra-ui/icons';

// Enhanced animations
const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-15px); }
`;

const fadeIn = keyframes`
  0% { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const UnauthorizedAccessPage = () => {
	const iconColor = useColorModeValue('red.500', 'red.400');
	const textColor = useColorModeValue('gray.600', 'gray.300');
	const headingColor = useColorModeValue('gray.800', 'white');

	const containerSize = useBreakpointValue({ base: 'sm', md: 'md', lg: 'lg' });
	const iconSize = useBreakpointValue({ base: 12, md: 16, lg: 20 });

	return (
		<Box
			width='100%'
			minH='100%'
			background={'white'}
			display='flex'
			alignItems='center'
			justifyContent='center'
			px={4}
			position='relative'
			borderRadius={'md'}
			overflow='hidden'>
			{/* Background decoration */}
			<Box
				position='absolute'
				top='10%'
				left='10%'
				width='200px'
				height='200px'
				borderRadius='full'
				background='rgba(255, 255, 255, 0.1)'
				filter='blur(40px)'
			/>
			<Box
				position='absolute'
				bottom='10%'
				right='10%'
				width='150px'
				height='150px'
				borderRadius='full'
				background='rgba(255, 255, 255, 0.05)'
				filter='blur(30px)'
			/>

			<Container maxW={containerSize}>
				<VStack
					spacing={8}
					p={8}
					// background={cardBg}
					borderRadius='2xl'
					// boxShadow='sm'
					// border='1px solid'
					// borderColor={useColorModeValue('gray.200', 'gray.600')}
					align='center'
					animation={`${fadeIn} 0.8s ease-out`}
					position='relative'
					_before={{
						content: '""',
						position: 'absolute',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						borderRadius: '2xl',
						padding: '2px',
						// background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
						mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
						maskComposite: 'exclude',
					}}>
					{/* Icon Container */}
					<Flex
						position='relative'
						justify='center'
						align='center'
						w='120px'
						h='120px'
						borderRadius='full'
						background={useColorModeValue('red.50', 'red.900')}
						animation={`${pulse} 2s infinite`}>
						<Icon
							as={WarningTwoIcon}
							boxSize={iconSize}
							color={iconColor}
							animation={`${bounce} 1.5s infinite`}
						/>
						{/* Secondary icon for visual interest */}
						<Icon
							as={LockIcon}
							boxSize={6}
							color={iconColor}
							position='absolute'
							bottom='10px'
							right='10px'
							opacity={0.7}
						/>
					</Flex>

					{/* Content */}
					<VStack spacing={4} textAlign='center'>
						<Heading
							as='h1'
							size='xl'
							color={headingColor}
							fontWeight='bold'
							lineHeight='1.2'>
							Access Restricted
						</Heading>

						<Text fontSize='lg' color={textColor} maxW='400px' lineHeight='1.6'>
							Oops! It looks like you don't have the necessary permissions to access
							this page. This area is reserved for authorized users only.
						</Text>

						<Text
							fontSize='sm'
							color={useColorModeValue('gray.500', 'gray.400')}
							fontStyle='italic'>
							If you believe this is an error, please contact your company
							administrator.
						</Text>
					</VStack>

					{/* Action Button */}
					<Button
						colorScheme='teal'
						size='lg'
						onClick={() => (window.location.href = '/')}
						borderRadius='full'
						px={8}
						py={3}
						fontWeight='semibold'
						fontSize='md'
						_hover={{
							transform: 'translateY(-2px)',
							boxShadow: 'lg',
						}}
						_active={{
							transform: 'translateY(0)',
						}}
						transition='all 0.3s ease'
						boxShadow='md'>
						Return to Home
					</Button>
				</VStack>
			</Container>
		</Box>
	);
};

export default UnauthorizedAccessPage;
